import PublicLayout from '@/layouts/PublicLayout';
import { Box, Container, Grid, Card, Typography, Avatar, Divider } from '@mui/material';
import { motion } from 'framer-motion';

// Import team member images
import samImage from '@/assets/team/Sam.jpg';
import elijahImage from '@/assets/team/Elijah.jpg';
import sammyImage from '@/assets/team/Sammy.jpg';
import azeemImage from '@/assets/team/Azeem.jpg';
import carlosImage from '@/assets/team/carlos.jpg';
import natImage from '@/assets/team/nat.jpg';
import jasImage from '@/assets/team/Jas.jpg';

const teamMembers = [
  {
    name: 'Samuel Ifeadi',
    role: 'Chief Executive Officer & Co-Founder',
    image: samImage,
    bio: 'Driving Dreamz Connect\'s vision and growth through strategic leadership and innovation.'
  },
  {
    name: 'Elijah De Los Santos',
    role: 'Chief Creative Officer & Co-Founder',
    image: elijahImage,
    bio: 'Creative strategist focused on creating meaningful content.'
  },
  {
    name: 'Sammy Basha',
    role: 'Chief Technology Officer & Co-Founder',
    image: sammyImage,
    bio: 'Technology Executive with extensive experiencing in digital transformation.'
  },
  {
    name: 'Azeem Green',
    role: 'Chief Financial Officer',
    image: azeemImage,
    bio: 'Focused on building the financial foundation for Dreamz Connect.'
  },
  {
    name: 'Carlos Alvarado',
    role: 'Chief Operations Officer',
    image: carlosImage,
    bio: 'Operations Executive with extensive experiencing in Technology and Operations.'
  },
  {
    name: 'Natalie Fay',
    role: 'Head of Design',
    image: natImage,
    bio: 'Design Leader experienced in building meaningful design experiences and meaningful relationships with youth.'
  },
  {
    name: 'Jasleen Gill',
    role: 'Operations Manager',
    image: jasImage,
    bio: 'Experienced in building meaningful partnerships with schools and organizations.'
  }
];

const TeamPage = () => {
  return (
    <PublicLayout>
      <Box sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #F7FAFC 0%, #EDF2F7 100%)',
        py: { xs: 4, md: 8 }
      }}>
        <Container 
          maxWidth="xl" 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          {/* Team Introduction */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            sx={{ 
              maxWidth: '800px',
              width: '100%',
              textAlign: 'center',
              mb: { xs: 8, md: 12 }
            }}
          >
            <Typography
              component="span"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 2,
                display: 'block'
              }}
            >
              Our Leadership Team
            </Typography>
            <Typography variant="h1" sx={{ mb: 3, fontWeight: 800 }}>
              Meet the Innovators Behind{' '}
              <Box 
                component="span"
                sx={{ 
                  background: 'linear-gradient(45deg, #4C51BF, #6B46C1)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Dreamz Connect
              </Box>
            </Typography>
            <Typography variant="h5" color="text.secondary">
              A passionate team dedicated to transforming education and volunteering
            </Typography>
          </Box>

          {/* Team Members Grid */}
          <Box
            sx={{ 
              maxWidth: '1600px',
              width: '100%',
              mx: 'auto'
            }}
          >
            <Grid 
              container 
              spacing={4} 
              alignItems="stretch"
              justifyContent="center"
            >
              {teamMembers.map((member, index) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={4} 
                  key={member.name}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'stretch',
                    width: '100%'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: { xs: '340px', sm: '100%' },
                      display: 'flex'
                    }}
                  >
                    <Card
                      component={motion.div}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '24px',
                        overflow: 'hidden',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                        }
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          p: 4,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          position: 'relative',
                          background: 'linear-gradient(45deg, rgba(76, 81, 191, 0.1), rgba(107, 70, 193, 0.1))',
                        }}
                      >
                        <Avatar
                          src={member.image}
                          alt={member.name}
                          sx={{
                            width: 120,
                            height: 120,
                            mb: 2,
                            border: '4px solid white',
                            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
                          }}
                        />
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, textAlign: 'center' }}>
                          {member.name}
                        </Typography>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            color: 'primary.main',
                            fontWeight: 600,
                            mb: 2,
                            textAlign: 'center',
                            px: 2
                          }}
                        >
                          {member.role}
                        </Typography>
                        <Box sx={{ 
                          flex: 1, 
                          display: 'flex', 
                          flexDirection: 'column', 
                          justifyContent: 'flex-start', 
                          width: '100%',
                          alignItems: 'center'
                        }}>
                          <Divider sx={{ width: '40%', mb: 2 }} />
                          <Typography 
                            variant="body1" 
                            color="text.secondary" 
                            sx={{ 
                              textAlign: 'center',
                              mt: 'auto',
                              maxWidth: '90%'
                            }}
                          >
                            {member.bio}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>
    </PublicLayout>
  );
};

export default TeamPage;